import React, {
    useRef,
    useState,
    useCallback,
    useMemo,
    useEffect,
    MutableRefObject,
    Ref,
    forwardRef,
    useImperativeHandle,
} from "react";
import _ from "lodash";
import classNames from "classnames";
import ReactPlayer from "react-player/lazy";
import { Player } from "video-react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { PlayIcon } from "components/icons/play";
import { PauseIcon } from "components/icons/pause";
import VideoPoster from "components/product-media/video-poster";
import IconButton from "@material-ui/core/IconButton";
import "video-react/dist/video-react.css";
import { DATATEST } from "constants/integration";

// prettier-ignore
interface IProps {
    source: string;
    ref?: MutableRefObject<IVideoRef>;
    poster?: string;
    className?: string;
    classes?: {
        root?: string;
        poster?: string;
        player?: string;
        /** used if customPlayer is true, passed to the root element because of incorrect support of className in video-react */
        custom?: string;
    };
    /** enable player mode without controls. Does not work with customPlayer=true */
    lightMode?: boolean;
    loop?: boolean;
    /** enable video autoPlay. Default to false */
    autoPlay?: boolean;
    /** mute video on load. Default to false */
    muted?: boolean;
    /** enable video download button. Default to false */
    download?: boolean;
    /** enable full screen button. Default to true */
    fullScreen?: boolean;
    /** enable playback. Default to true */
    remotePlayback?: boolean;
    /** enable context menu by mouse right click. Default to false */
    contextMenu?: boolean;
    /** enable picture in picture feature. Default to false */
    pictureInPicture?: boolean;
    /** switch to custom video player mode. Used mostly to avoid safari playback issue */
    customPlayer?: boolean;

    videoRef?: React.MutableRefObject<HTMLDivElement>
    onProgress?: (state: any) => void;
    onDuration?: (duration: any) => void;
    height?: string;
}

// prettier-ignore
export interface IVideoRef {
    play (): void;
    stop (): void;
    pause (): void;
    seek (time: number): void;
}

/** HTML5 video controls to disable internal features */
const CONTROLS = {
    download: "nodownload",
    fullScreen: "nofullscreen",
    remotePlayback: "noremoteplayback",
};

export const VideoPlayer = forwardRef<IVideoRef, IProps>((props: IProps, ref: Ref<IVideoRef>) => {
    const { source, poster, lightMode, customPlayer, loop, videoRef, onProgress, onDuration } = props;
    const { className, classes = {} } = props;
    const {
        fullScreen = true,
        remotePlayback = true,
        autoPlay = false,
        muted = false,
        pictureInPicture = false,
        download,
        contextMenu,
        height,
    } = props;

    const [started, setStarted] = useState(autoPlay);
    const [playing, setPlaying] = useState(autoPlay);
    const [hover, setHover] = useState(false);

    const player = useRef<ReactPlayer>();
    const customPlayerRef = useRef<Player>();

    // prettier-ignore
    useImperativeHandle<IVideoRef, IVideoRef>(ref, () => ({
        play: () => {
            handlePlay();
        },
        stop: () => {
            setStarted(false);
            setPlaying(false);
        },
        pause: () => {
            setPlaying(false);
            customPlayerRef.current?.video?.handlePause();
        },
        seek: (time: number) => {
            player.current?.seekTo(time);
        },
    }), [customPlayerRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const videoPlayerConfig = useMemo(() => {
        if (lightMode) {
            return {};
        }
        const controlsList = _({ download, fullScreen, remotePlayback })
            .map((value, key) => (!value ? CONTROLS[key] : null))
            .compact()
            .join(" ");
        return {
            file: {
                attributes: {
                    controlsList,
                    muted,
                    disablepictureinpicture: `${!pictureInPicture}`,
                    autoPlay: true, // fix for IPads and Safari browser
                    onContextMenu: (e) => (!contextMenu ? e.preventDefault() : null),
                },
            },
        };
    }, [lightMode, download, fullScreen, remotePlayback, muted, pictureInPicture, contextMenu]);

    const handlePlay = useCallback(() => {
        setStarted(true);
        setPlaying(true);
    }, []);
    const handlePause = useCallback(() => setPlaying(false), []);

    const handleHover = useCallback((hovered: boolean) => () => setHover(lightMode && hovered), [lightMode]);
    const handleEnded = useCallback(() => {
        setPlaying(false);
        if (!loop) {
            player.current.seekTo(0); // set video to beginning
        }
    }, [loop, player]);

    const handleProgress = useCallback(
        (state) => {
            if (onProgress) {
                onProgress(state);
            }
        },
        [onProgress],
    );

    const handleDuration = useCallback(
        (duration) => {
            if (onDuration) {
                onDuration(duration);
            }
        },
        [onDuration],
    );

    // autoplay custom player on start
    useEffect(() => {
        if (customPlayerRef?.current && started) {
            customPlayerRef.current.video?.handlePlay();
        }
    }, [customPlayer, customPlayerRef.current, started]); // eslint-disable-line react-hooks/exhaustive-deps

    const inner = useStyles({});
    return (
        <div
            className={classNames(
                inner.root,
                className,
                classes.root,
                customPlayer && classNames(inner.custom, classes.custom),
            )}
            onMouseEnter={handleHover(true)}
            onMouseLeave={handleHover(false)}
            ref={videoRef}
            data-test={DATATEST.PDP.JUMBO_VIDEO}
        >
            {!started && <VideoPoster poster={poster} className={classes.poster} height={height} />}
            {(!started || (lightMode && hover)) && (
                <div
                    className={inner.actionHandler}
                    onClick={playing ? handlePause : handlePlay}
                    data-test={DATATEST.PDP.JUMBO_VIDEO_PLAY_PAUSE}
                >
                    <IconButton className={inner.button}>
                        {playing ? <PauseIcon className={inner.icon} /> : <PlayIcon className={inner.icon} />}
                    </IconButton>
                </div>
            )}
            {!customPlayer && started && (
                <ReactPlayer
                    ref={player}
                    url={source}
                    className={classNames(inner.player, classes.player)}
                    playing={playing}
                    controls={!lightMode}
                    loop={loop}
                    width="100%"
                    height={height ?? "100%"}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                    config={videoPlayerConfig}
                    pip={pictureInPicture}
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                />
            )}
            {/* avoid safari playback bug - use custom player instead of ReactPlayer <video> wrapper */}
            {customPlayer && started && (
                <Player
                    ref={customPlayerRef}
                    src={source}
                    poster={poster}
                    preload="none"
                    muted={muted}
                    playsInline={true}
                    autoPlay={true}
                    fluid={false}
                    loop={loop}
                />
            )}
        </div>
    );
});

const useStyles = makeStyles({
    root: {
        position: "relative",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    actionHandler: {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 1000,
        border: "none",
        outline: "none",
        background: "transparent",
    },
    button: {
        position: "absolute",
        zIndex: 10,
        padding: 0,
    },
    icon: {
        cursor: "pointer",
        width: "60px !important",
        height: "60px !important",
    },
    player: {
        display: "flex",
        justifyContent: "center",
        "& video": {
            objectFit: "contain",
        },
    },
    custom: {
        "& .video-react": {
            paddingTop: "0px !important",
            height: "100% !important",
            width: "100% !important",
            display: "flex",
        },
        "& .video-react-big-play-button": {
            display: "none",
        },
        "& .video-react-poster": {
            display: "none",
        },
        "& .video-react-video": {
            position: "relative",
        },
    },
});

export default VideoPlayer;
